/**
 * Shared Axios client for FxBazzar REST (`/api/v1`).
 * Auth against ENV.LIVE_API (login / demo / reset-password) uses services/authService.js instead.
 */
import axios from "axios";
import { ENV } from "../config/env";
import { isAuthCookieSession, refreshAuthCookiesOnce } from "../services/authService";
import {
  applyEncryptionHeaders,
  decryptFromEnvelopeJson,
  encryptToEnvelopeJson,
  encryptTokenToBase64Envelope,
  isApiCryptoEnabled,
  looksLikeEnvelope,
  isTimeInfoRequest,
} from "./apiCrypto";

const API_BASE = process.env.REACT_APP_API_BASE || "";

export const api = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  withCredentials: true,
  timeout: 15000,
});

export const setAuthToken = (token) => {
  if (isAuthCookieSession()) {
    delete api.defaults.headers.common.Authorization;
    return;
  }
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
};

api.interceptors.request.use(
  async (config) => {
    config.headers = config.headers || {};
    config.withCredentials = isAuthCookieSession();

    // HttpOnly fxb_access is sent by the browser; do not duplicate JWT in Authorization (XSS-safe).
    if (isAuthCookieSession()) {
      delete config.headers.Authorization;
      delete api.defaults.headers.common.Authorization;
    } else if (!config.headers.Authorization) {
      try {
        const raw =
          sessionStorage.getItem("fxbazzar_user") ||
          sessionStorage.getItem("fxbazzar_admin") ||
          "{}";
        const d = JSON.parse(raw);
        const token = d.token || d.Token || "";
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        /* ignore */
      }
    }
    if (isApiCryptoEnabled() && !isTimeInfoRequest(config)) {
      const method = String(config.method || "get").toUpperCase();
      const hasJsonBodyMethod = method === "POST" || method === "PUT" || method === "PATCH";
      const isFormData = typeof FormData !== "undefined" && config.data instanceof FormData;

      if (hasJsonBodyMethod && config.data != null && !isFormData) {
        const plain =
          typeof config.data === "string" ? config.data : JSON.stringify(config.data);
        config.data = JSON.parse(await encryptToEnvelopeJson(plain));
        applyEncryptionHeaders(config.headers, { encryptedBody: true, encryptedResponse: true });
      } else {
        applyEncryptionHeaders(config.headers, { encryptedResponse: true });
      }

      const authHeader = String(config.headers.Authorization || "");
      if (authHeader.startsWith("Bearer ")) {
        const token = authHeader.slice(7).trim();
        if (token && !token.startsWith("eyJ")) {
          applyEncryptionHeaders(config.headers, { encryptedAuth: true });
        } else if (token) {
          const encToken = await encryptTokenToBase64Envelope(token);
          config.headers.Authorization = `Bearer ${encToken}`;
          applyEncryptionHeaders(config.headers, { encryptedAuth: true });
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  async (response) => {
    if (isApiCryptoEnabled() && looksLikeEnvelope(response?.data)) {
      const plain = await decryptFromEnvelopeJson(response.data);
      response.data = JSON.parse(plain);
    }
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    if (status && status !== 404) {
      console.log("API ERROR STATUS:", status);
    }

    const original = error.config;
    const url = String(original?.url || "");
    const isRefreshCall = url.includes("user/refresh-token");

    if (
      status === 401 &&
      isAuthCookieSession() &&
      original &&
      !original.__fxbRetried401 &&
      !isRefreshCall
    ) {
      original.__fxbRetried401 = true;
      const refreshed = await refreshAuthCookiesOnce();
      if (refreshed) {
        try {
          return await api.request(original);
        } catch (e) {
          return Promise.reject(e);
        }
      }
    }

    if (error.response?.status === 401) {
      console.warn("Session expired — redirecting to login");
      sessionStorage.removeItem("fxbazzar_user");
      sessionStorage.removeItem("fxbazzar_admin");
      if (window.location.pathname !== "/") {
        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  }
);
